import { CommonModule } from "@angular/common";
import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { PageBreadcrumbComponent } from "../../../shared/components/common/page-breadcrumb/page-breadcrumb.component";
import { BadgeComponent } from "../../../shared/components/ui/badge/badge.component";
import { ButtonComponent } from "../../../shared/components/ui/button/button.component";
import { MeetingFilterOption, MeetingRequest, MeetingResponse, MeetingSearchParams, MeetingStatusResponse, MeetingTypeResponse, MEETING_PAYMENT_STATUS_LABELS, MEETING_SOURCE_LABELS, meetingLabel } from "../models/meeting.model";
import { MeetingApiService } from "../services/meeting-api.service";
import { MeetingTypeApiService } from "../services/meeting-type-api.service";
import { MeetingStatusApiService } from "../services/meeting-status-api.service";
import { AuthService } from "../../auth-pages/services/auth.service";
import { MeetingDetailModalComponent } from "../meeting-detail-modal/meeting-detail-modal.component";
import { MeetingCancelDialogComponent } from "../shared/meeting-cancel-dialog/meeting-cancel-dialog.component";
import { MeetingRescheduleDialogComponent } from "../shared/meeting-reschedule-dialog/meeting-reschedule-dialog.component";
import { JitsiRoomComponent } from "../jitsi-room/jitsi-room.component";
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { isMeetingCancelled } from '../shared/meeting-action.util';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component ({
    selector: 'app-meeting-list',
    standalone: true,
    imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ButtonComponent,
    MeetingDetailModalComponent,
    MeetingCancelDialogComponent,
    MeetingRescheduleDialogComponent,
    JitsiRoomComponent,
    HasPermissionDirective
],
    templateUrl: './meeting-list.component.html',
    
})

export class MeetingListComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    meetings: MeetingResponse[] = [];
    loading = false;
    error = '';

    // Modal state
  showDetailModal = false;
  selectedMeeting: MeetingResponse | null = null;

  // Acciones cancelar / reprogramar (INC 48)
  showCancelDialog = false;
  showRescheduleDialog = false;
  accionMeeting: MeetingResponse | null = null;

  // Catálogos para filtros — por nombre, no por ID (INC 22/24/25)
  meetingTypes: MeetingTypeResponse[] = [];
  meetingStatuses: MeetingStatusResponse[] = [];
  // INC 68: opciones acotadas a los involucrados en reuniones (endpoints /meetings/filters/*)
  contacts: MeetingFilterOption[] = [];
  companies: MeetingFilterOption[] = [];
  deals: MeetingFilterOption[] = [];
  organizers: MeetingFilterOption[] = [];


  // Filtros
  showFilters = false;
  filtroSearchTerm = '';
  filtroIdMeetingType: number | undefined;
  filtroIdMeetingStatus: number | undefined;
  filtroContactId: number | undefined;
  filtroCompanyId: number | undefined;
  filtroDealId: number | undefined;
  filtroOrganizerId: number | undefined;
  filtroDateFrom: string | undefined;
  filtroDateTo: string | undefined;

  // Filtros avanzados B13
  filtroTimeRange: 'all' | 'upcoming' | 'past' = 'all';
  filtroIsNoShow: '' | 'true' | 'false' = '';
  filtroPaymentStatus: '' | 'pending' | 'paid' | 'refunded' | 'failed' = '';
  filtroSource: '' | 'manual' | 'booking_page' | 'api' | 'import' = '';

  // Paginacion
  currentPage = 0; // Spring Data usa 0-based
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  constructor(
    private meetingApi: MeetingApiService,
    private meetingTypeApi: MeetingTypeApiService,
    private meetingStatusApi: MeetingStatusApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarMeetings();
    this.cargarCatalogosFiltros();

    // INC 83: al llegar con ?openMeeting=<id> (desde la notificación "Nueva reunión
    // agendada") se abre el modal de DETALLE sobre la lista. Antes la notificación
    // navegaba a /meeting/editar/:id y metía al usuario directo al formulario.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const raw = params.get('openMeeting');
      const id = raw != null ? Number(raw) : NaN;
      if (!Number.isNaN(id)) {
        this.abrirDetalleModalPorId(id);
      }
    });
  }

  /** INC 83: carga la reunión por id y abre el modal de detalle. */
  private abrirDetalleModalPorId(id: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.meetingApi.getMeetingById(id, businessId).subscribe({
      next: (meeting) => {
        this.selectedMeeting = meeting;
        this.showDetailModal = true;
      },
      error: () => {
        this.toast.error({
          title: 'No se pudo abrir la reunión',
          message: 'La reunión no existe o no tienes acceso.'
        });
      }
    });
  }

  // INC 68: los filtros de Contacto/Empresa/Negocio/Organizador se alimentan de
  // los endpoints "involucrados" (listas cortas), no de listas completas del sistema.
  private cargarCatalogosFiltros(): void {
    this.meetingApi.getFilterContacts().subscribe({
      next: (r) => this.contacts = r || [],
      error: () => console.error('Error cargando contactos involucrados'),
    });

    this.meetingApi.getFilterCompanies().subscribe({
      next: (r) => this.companies = r || [],
      error: () => console.error('Error cargando empresas involucradas'),
    });

    this.meetingApi.getFilterDeals().subscribe({
      next: (r) => this.deals = r || [],
      error: () => console.error('Error cargando negocios involucrados'),
    });

    this.meetingApi.getFilterOrganizers().subscribe({
      next: (r) => this.organizers = r || [],
      error: () => console.error('Error cargando organizadores involucrados'),
    });
  }

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.meetingTypeApi.listActive(businessId).subscribe({
      next: (data) => this.meetingTypes = data,
      error: () => console.log('Error cargando tipos')
    });

    this.meetingStatusApi.listActive(businessId).subscribe({
      next: (data) => this.meetingStatuses = data,
      error: () => console.log('Error cargando estados')
    });
 }

  cargarMeetings(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: MeetingSearchParams = {
      businessId,
      searchTitle: this.filtroSearchTerm || undefined,
      idMeetingType: this.filtroIdMeetingType,
      idMeetingStatus: this.filtroIdMeetingStatus,
      contactId: this.filtroContactId,
      companyId: this.filtroCompanyId,
      dealId: this.filtroDealId,
      organizerId: this.filtroOrganizerId,
      datetimeStartFrom: this.dateFilterToIso(this.filtroDateFrom, false),
      datetimeStartTo: this.dateFilterToIso(this.filtroDateTo, true),
      upcomingOnly: this.filtroTimeRange === 'upcoming' ? true : undefined,
      pastOnly: this.filtroTimeRange === 'past' ? true : undefined,
      isNoShow:
        this.filtroIsNoShow === '' ? undefined : this.filtroIsNoShow === 'true',
      paymentStatus: this.filtroPaymentStatus || undefined,
      source: this.filtroSource || undefined,
      page: this.currentPage,
      size: this.pageSize,
      sortBy: 'datetimeStart',
      sortDirection: 'desc'
    };

    this.meetingApi.searchMeetings(params).subscribe({
      next: (response) => {
        this.meetings = response.content;
        this.totalItems = response.totalElements;
        this.totalPages = response.totalPages;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar reuniones';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  // El backend espera OffsetDateTime; un "YYYY-MM-DD" plano responde "Type mismatch."
  private dateFilterToIso(dateStr: string | undefined, endOfDay: boolean): string | undefined {
    if (!dateStr) return undefined;
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = endOfDay
      ? new Date(y, m - 1, d, 23, 59, 59)
      : new Date(y, m - 1, d, 0, 0, 0);
    return date.toISOString().slice(0, 19) + 'Z';
  }

  buscar(): void {
    this.currentPage = 0;
    this.cargarMeetings();
  }

  limpiarFiltro(): void {
    this.filtroSearchTerm = '';
    this.filtroIdMeetingType = undefined;
    this.filtroIdMeetingStatus = undefined;
    this.filtroContactId = undefined;
    this.filtroCompanyId = undefined;
    this.filtroDealId = undefined;
    this.filtroOrganizerId = undefined;
    this.filtroDateFrom = undefined;
    this.filtroDateTo = undefined;
    this.filtroTimeRange = 'all';
    this.filtroIsNoShow = '';
    this.filtroPaymentStatus = '';
    this.filtroSource = '';
    this.currentPage = 0;
    this.cargarMeetings();
  }

  nuevaReunion(): void {
    this.router.navigate(['/meeting/crear']);
  }

  // ─── Reunión instantánea (crear Jitsi + entrar en un clic) ─────────
  creandoInstantanea = false;

  crearReunionInstantanea(): void {
    if (this.creandoInstantanea) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const now = new Date();
    now.setSeconds(0, 0);
    const end = new Date(now.getTime() + 30 * 60000);

    const horaCorta = now.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    const request: MeetingRequest = {
      title: `Reunión instantánea ${horaCorta}`,
      datetimeStart: now.toISOString(),
      datetimeEnd: end.toISOString(),
      businessId,
      videoProvider: 'jitsi',
      organizerId: user?.id ? +user.id : undefined,
    };

    this.creandoInstantanea = true;
    this.meetingApi.createMeeting(request).subscribe({
      next: (meeting) => {
        this.creandoInstantanea = false;
        this.toast.created('Reunión');
        // El backend omite el correo de confirmación al cliente si la reunión
        // quedó sin enlace y sin lugar: aviso persistente (no se autocierra).
        if (meeting.confirmationEmailSkipped === true) {
          this.toast.warning({
            title: 'El correo de confirmación NO se envió al cliente',
            message:
              meeting.confirmationEmailSkippedReason ||
              'La reunión quedó sin enlace de videollamada y sin lugar presencial. Edítala para completar la modalidad y vuelve a guardar.',
            duration: 0,
          });
        }
        if (this.canJoinJitsi(meeting)) {
          this.jitsiMeeting = meeting;
        } else {
          // El back no devolvió sala Jitsi: avisar y refrescar igualmente.
          this.toast.warning({
            title: 'Reunión creada sin sala',
            message: 'No se generó la sala de video. Ábrela desde el detalle.',
          });
          this.cargarMeetings();
        }
      },
      error: (err) => {
        this.creandoInstantanea = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo crear la reunión instantánea',
        });
      },
    });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  editarReunion(meeting: MeetingResponse): void {
    this.router.navigate(['/meeting/editar', meeting.id]);
  }

  // ─── Acciones Reprogramar / Cancelar (INC 48) ──────────────────
  reprogramarReunion(meeting: MeetingResponse): void {
    this.accionMeeting = meeting;
    this.showRescheduleDialog = true;
  }

  cancelarReunion(meeting: MeetingResponse): void {
    this.accionMeeting = meeting;
    this.showCancelDialog = true;
  }

  cerrarCancelDialog(): void {
    this.showCancelDialog = false;
    this.accionMeeting = null;
  }

  cerrarRescheduleDialog(): void {
    this.showRescheduleDialog = false;
    this.accionMeeting = null;
  }

  onAccionConfirmada(): void {
    this.showCancelDialog = false;
    this.showRescheduleDialog = false;
    this.accionMeeting = null;
    this.cargarMeetings();
  }

  async eliminarReunion(meeting: MeetingResponse): Promise<void> {
    if (!meeting.id) return;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar reunión',
      message: `¿Está seguro de eliminar la reunión "${meeting.title}"?`
    });
    if (!ok) return;
    this.meetingApi.deleteMeeting(meeting.id, businessId).subscribe({
      next: () => {
        this.cargarMeetings();
        this.toast.deleted('Reunión');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar la reunión' });
      }
    });
  }

   // Métodos auxiliares
    getMeetingTypeName(meeting: MeetingResponse): string {
        if (!meeting.idMeetingType) return 'Sin tipo';
        const type = this.meetingTypes.find(t => t.id === meeting.idMeetingType);
        return type?.label || type?.name || 'Sin tipo';
    }

    getPaymentBadgeColor(status?: string): BadgeColor {
        switch (status) {
            case 'paid': return 'success';
            case 'pending': return 'warning';
            case 'refunded': return 'info';
            case 'failed': return 'error';
            default: return 'light';
        }
    }

    getSourceLabel(source?: string): string {
        return meetingLabel(MEETING_SOURCE_LABELS, source, '');
    }

    getPaymentStatusLabel(status?: string | null): string {
        return meetingLabel(MEETING_PAYMENT_STATUS_LABELS, status);
    }

    getMeetingStatusName(meeting: MeetingResponse): string {
        // INC 80: una reunión cancelada se ve como tal aunque el negocio no tenga un
        // estado "Cancelada" (al cancelar solo queda el motivo y antes seguía saliendo
        // "Confirmado", mientras el backend ya la trataba como cancelada).
        if (this.estaCancelada(meeting)) return 'Cancelada';
        if (!meeting.idMeetingStatus) return 'Sin estado';
        const status = this.meetingStatuses.find(s => s.id === meeting.idMeetingStatus);
        return status?.label || status?.name || 'Sin estado';
    }

    /** INC 80: criterio único de cancelación (mismo que el backend). */
    estaCancelada(meeting: MeetingResponse): boolean {
        return isMeetingCancelled(meeting, this.meetingStatuses);
    }

    /** Estado real bajo una reunión cancelada, para el tooltip. */
    estadoOriginal(meeting: MeetingResponse): string {
        if (!meeting.idMeetingStatus) return '';
        const status = this.meetingStatuses.find(s => s.id === meeting.idMeetingStatus);
        const nombre = status?.label || status?.name;
        return nombre ? `Estado en el catálogo: ${nombre}` : '';
    }

    getContactName(contactId: number | null | undefined): string {
    if (!contactId) return 'Sin contacto';

    const contact = this.contacts.find(c => c.id === contactId);
    if (!contact) return `ID: ${contactId}`;
    return contact.name || `ID: ${contactId}`;
    }

    getStatusColor(meeting: MeetingResponse): BadgeColor {
        if (this.estaCancelada(meeting)) return 'error'; // INC 80
        if (!meeting.idMeetingStatus) return 'light';
        
        // Aquí puedes mapear estados específicos a colores
        const statusId = meeting.idMeetingStatus;
        
        // Ejemplo:
        if (statusId === 1) return 'success'; // Completada
        if (statusId === 2) return 'warning'; // Pendiente
        if (statusId === 3) return 'error';   // Cancelada
        
        return 'info';
    }

    formatFecha(dateStr: string): string {
        const date = new Date(dateStr);
        return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
        });
    }

    formatFechaCorta(dateStr: string): string {
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
        });
    }

    formatHora(dateStr: string | undefined): string {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
        });
    }

    // Paginacion
    get paginasDisplay(): number {
        return this.currentPage + 1;
    }

    cambiarPagina(page: number): void {
        const pageIndex = page - 1;
        if (pageIndex >= 0 && pageIndex < this.totalPages) {
        this.currentPage = pageIndex;
        this.cargarMeetings();
        }
    }

    verDetalle(meeting: MeetingResponse): void {
        this.selectedMeeting = meeting;
        this.showDetailModal = true;
    }

    cerrarModal(): void {
        this.showDetailModal = false;
        this.selectedMeeting = null;
        // INC 83: limpiar ?openMeeting para poder reabrir la misma reunión después.
        if (this.route.snapshot.queryParamMap.has('openMeeting')) {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { openMeeting: null },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }
    }

    // ─── Videollamada Jitsi inline (organizador) ───────────────────
    jitsiMeeting: MeetingResponse | null = null;

    canJoinJitsi(meeting: MeetingResponse): boolean {
        return meeting.videoProvider === 'jitsi' && !!meeting.videoRoomToken;
    }

    jitsiRoomName(): string {
        return this.jitsiMeeting?.videoRoomToken
            ? `partnercrm-${this.jitsiMeeting.videoRoomToken}`
            : '';
    }

    jitsiDisplayName(): string {
        const u = this.authService.getCurrentUser();
        return `${u?.fullname || ''} ${u?.lastname || ''}`.trim() || 'Organizador';
    }

    jitsiEmail(): string {
        return this.authService.getCurrentUser()?.email || '';
    }

    unirseJitsi(meeting: MeetingResponse): void {
        if (this.canJoinJitsi(meeting)) this.jitsiMeeting = meeting;
    }

    cerrarJitsi(): void {
        this.jitsiMeeting = null;
        // Refrescar la lista al salir (una reunión instantánea recién creada debe aparecer).
        this.cargarMeetings();
    }

    get hasPreviousPage(): boolean {
        return this.currentPage > 0;
    }

    get hasNextPage(): boolean {
        return this.currentPage < this.totalPages - 1;
    }

    // Para búsqueda de contactos (si se necesita)
    buscarContactos(): void {
        // TODO: Implementar búsqueda de contactos
        // Podría abrir un modal selector de contactos
    }

    // Para búsqueda de deals (si se necesita)
    buscarDeals(): void {
        // TODO: Implementar búsqueda de deals
    }
    
    irAKanban(): void {
  this.router.navigate(['/meeting/kanban']);
}

}